import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RouterOutlet } from "@angular/router";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { filter } from "rxjs/operators";
import { AlertsComponent } from "./common/alerts/alerts.component";
import { MapsLoaderService } from "./services/maps-loader.service";

@Component({
  selector: "app-root",
  imports: [RouterOutlet, AlertsComponent],
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnInit {
  title = "AIagent";

  private readonly swUpdate = inject(SwUpdate);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mapsLoader = inject(MapsLoaderService);

  ngOnInit(): void {
    // Kick off Google Maps SDK loading at bootstrap. Same async semantics as
    // the previous static `<script>` tag in index.html, but the API key now
    // comes from `environment.googleMapsApiKey` (build-time replacement)
    // instead of being hard-coded.
    this.mapsLoader.load().catch(() => {
      // Silent — components that depend on Maps already need to handle a
      // missing `google.maps` global gracefully (script was `async` before).
    });

    // Service-worker version handling — pairs with `ChunkErrorHandler` so the
    // user never lands on an `index.html` that references chunk hashes the
    // server no longer has. Subscribe to VERSION_READY → silent reload, and
    // poll for updates every 5 minutes so long-lived tabs don't drift past a
    // deploy.
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === "VERSION_READY"),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => document.location.reload());

    const intervalId = window.setInterval(
      () => this.swUpdate.checkForUpdate().catch(() => {}),
      5 * 60_000,
    );
    this.destroyRef.onDestroy(() => window.clearInterval(intervalId));
  }
}